﻿import { FieldDefinition, isHidden } from '@/shared/codeedit/field.ts';
import { CodeResponse } from '@/shared/helpers/api/types/codeEditContextResponse.ts';
import { AttributeValueChoice } from '@/shared/helpers/api/types/entities.ts';

export type AttributeValue = string | number | boolean | null;
export type AttributeValues = Partial<Record<string, AttributeValue>>;

export type CodeEditSessionMetadata = {
    codeId: number;
    libraryName: string;
    codeSetName: string;
    isNew: boolean;
    deleteOnClearChanges?: boolean;
};

export type CodeEditFields = {
    identity: string;
    name: string;
    description: string | null;
    isValid: boolean;
    dateCreated: string;
    dateUpdated: string;
} & AttributeValues;

export function newCodeEditFields(fieldDefinitions: FieldDefinition[]): CodeEditFields {
    const attributes: AttributeValues = {};
    for (const fieldDefinition of fieldDefinitions) {
        if (fieldDefinition.defaultValue) {
            attributes[fieldDefinition.field] = fieldDefinition.defaultValue;
        } else if (fieldDefinition.type.toLowerCase() === 'bool') {
            attributes[fieldDefinition.field] = false;
        } else {
            attributes[fieldDefinition.field] = null;
        }
    }
    return {
        ...attributes,
        isValid: true,
        identity: '',
        name: '',
        description: null,
        dateCreated: '',
        dateUpdated: ''
    };
}

export function codeEditFieldsFromCodeResponse(code: CodeResponse, codeFields: string[]): CodeEditFields {
    const codeObj: CodeEditFields = {
        identity: code.identity,
        name: code.name,
        description: code.description,
        isValid: code.isValid,
        dateCreated: code.dateCreated,
        dateUpdated: code.dateUpdated
    };

    code.attributes.forEach(a => {
        codeObj[a.definitionName] = a.value;
    });

    for (const field of codeFields) {
        if (!(field in codeObj)) {
            codeObj[field] = null;
        }
    }
    return codeObj;
}

/**
 * Computes the changes between existing and edited code, returns undefined if there are no changes.
 * @throws {Error} If either provided CodeEditFields object (mod or base) is missing a field
 * defined in the provided fieldDefinitions.
 */
export function codeEditFieldsDiff(
    mod: CodeEditFields,
    base: CodeEditFields,
    fieldDefinitions: FieldDefinition[]): Record<string, AttributeValue> | undefined {
    const changes: Record<string, AttributeValue> = {};
    let hasChanges: boolean = false;

    for (const attr of fieldDefinitions) {
        const modifiedValue = mod[attr.field];
        const baseValue = base[attr.field];
        if (modifiedValue === undefined) {
            throw new Error(`modified value for attribute ${attr.field} is undefined`);
        }
        if (baseValue === undefined) {
            throw new Error(`base value for attribute ${attr.field} is undefined`);
        }
        if (modifiedValue !== baseValue) {
            changes[attr.field] = modifiedValue;
            hasChanges = true;
        }
    }

    if (hasChanges) {
        return changes;
    }
}

/**
 * Returns whether a visible required field is null or empty, excluding unchanged fields on existing codes.
 * @throws {Error} If either provided CodeEditFields object (mod or base) is missing a visible
 * required field defined in the provided fieldDefinitions.
 */
export function codeEditFieldsRequiredMissing(
    mod: CodeEditFields,
    base: CodeEditFields,
    fieldDefinitions: FieldDefinition[],
    metadata: CodeEditSessionMetadata): boolean {
    return fieldDefinitions
        .filter(a => !isHidden(a) && (a.required))
        .some((a) => {
            const modifiedValue = mod[a.field];
            const baseValue = base[a.field];
            if (modifiedValue === undefined) {
                throw new Error(`modified value for attribute ${a.field} is undefined`);
            }
            if (baseValue === undefined) {
                throw new Error(`base value for attribute ${a.field} is undefined`);
            }
            if (!metadata.isNew && modifiedValue === baseValue) {
                return false;
            }
            return modifiedValue === null || modifiedValue === '';
        });
}

/*
Disallow null, undefined, and empty string for required fields
 */
export function codeEditRequiredFieldValidValue(value?: AttributeValue): boolean {
    switch (value) {
        case null:
        case undefined:
        case '':
            return false;
        default:
            return true;
    }
}

// Returns true if the provided value is not a part of the choice list,
// and neither the value nor choice list is null/undefined/empty
export function choiceViolation(value?: AttributeValue, choices?: AttributeValueChoice[]): boolean {
    if (value === null || value === undefined || !choices?.length) {
        return false;
    }
    return !choices.some((c) => c.value === value);
}
